import { useState, useMemo, useEffect } from 'react';
import { apiFetch, useApi } from '../hooks/useApi.js';
import PrintButton from '../components/shared/PrintButton.jsx';
import { uiConfirm } from '../utils/dialogs.js';

// ── Shared UI primitives (matches Production.jsx / StorageUnits.jsx style) ───
function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const base = {
    borderRadius: 'var(--radius)', fontWeight: 600, cursor: 'pointer', border: 'none',
    fontSize: size === 'sm' ? 12 : 14,
    padding: size === 'sm' ? '5px 12px' : '9px 18px',
    opacity: props.disabled ? 0.5 : 1,
    transition: 'opacity .15s',
  };
  const v = {
    primary: { background: 'var(--pld-green)', color: '#fff' },
    ghost:   { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' },
    danger:  { background: '#dc2626', color: '#fff' },
  };
  return <button {...props} style={{ ...base, ...(v[variant] || v.primary), ...props.style }}>{children}</button>;
}

function Modal({ title, onClose, width = 480, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{label}</label>
      {children}
    </div>
  );
}

function Input(props) {
  return <input {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }} />;
}

function Select({ children, ...props }) {
  return <select {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)' }}>{children}</select>;
}

const LOCATION_TYPES = ['rack', 'floor', 'ibc_bay', 'cask_bay'];
const ZONE_TYPES = ['bonded', 'non_bonded', 'production', 'cask_store', 'dry_store', 'external'];

function fmtType(t) { return (t || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()); }

const TYPE_C = { rack: '#3b82f6', floor: '#6b7280', ibc_bay: '#8b5cf6', cask_bay: '#f59e0b' };
function TypeBadge({ type }) {
  const c = TYPE_C[type] || '#6b7280';
  return <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', background: c + '22', color: c }}>{fmtType(type)}</span>;
}

// ── Add Zone modal ─────────────────────────────────────────────────────────
function ZoneModal({ facilityId, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', code: '', zone_type: 'dry_store' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await apiFetch('/warehouse/zones', { method: 'POST', body: { ...form, facility_id: facilityId } });
      onSaved();
    } catch (e2) { setErr(e2.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal title="Add Zone" onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="Name *"><Input value={form.name} onChange={set('name')} placeholder="e.g. Left Racking" required /></Field>
        <Field label="Code *"><Input value={form.code} onChange={set('code')} placeholder="e.g. LR" required /></Field>
        <Field label="Zone type *">
          <Select value={form.zone_type} onChange={set('zone_type')} required>
            {ZONE_TYPES.map(t => <option key={t} value={t}>{fmtType(t)}</option>)}
          </Select>
        </Field>
        {err && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{err}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving…' : 'Add Zone'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Add / Edit Bay modal ──────────────────────────────────────────────────
function BayModal({ zoneId, bay, onClose, onSaved }) {
  const [form, setForm] = useState({
    code:          bay?.code ?? '',
    location_type: bay?.location_type ?? 'rack',
    row_ref:       bay?.row_ref ?? '',
    bay_ref:       bay?.bay_ref ?? '',
    level_ref:     bay?.level_ref ?? '',
    max_pallets:   bay?.max_pallets ?? '',
    max_weight_kg: bay?.max_weight_kg ?? '',
    notes:         bay?.notes ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      const body = {
        ...form,
        max_pallets:   form.max_pallets   ? parseInt(form.max_pallets)   : undefined,
        max_weight_kg: form.max_weight_kg ? parseInt(form.max_weight_kg) : undefined,
      };
      if (bay) {
        await apiFetch(`/warehouse/locations/${bay.id}`, { method: 'PUT', body });
      } else {
        await apiFetch('/warehouse/locations', { method: 'POST', body: { ...body, zone_id: zoneId } });
      }
      onSaved();
    } catch (e2) { setErr(e2.message); }
    finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!(await uiConfirm(`Delete bay "${bay.code}"? This won't remove any stock history, just the bay itself.`))) return;
    setSaving(true); setErr('');
    try {
      await apiFetch(`/warehouse/locations/${bay.id}`, { method: 'DELETE' });
      onSaved();
    } catch (e2) { setErr(e2.message); setSaving(false); }
  }

  return (
    <Modal title={bay ? 'Edit Bay' : 'Add Bay'} onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="Code *"><Input value={form.code} onChange={set('code')} placeholder="e.g. LR-B1-BOT" required /></Field>
        <Field label="Type *">
          <Select value={form.location_type} onChange={set('location_type')} required>
            {LOCATION_TYPES.map(t => <option key={t} value={t}>{fmtType(t)}</option>)}
          </Select>
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Field label="Row"><Input value={form.row_ref} onChange={set('row_ref')} placeholder="e.g. 1" /></Field>
          <Field label="Bay"><Input value={form.bay_ref} onChange={set('bay_ref')} placeholder="e.g. B1" /></Field>
          <Field label="Level"><Input value={form.level_ref} onChange={set('level_ref')} placeholder="Bottom/Middle/Top" /></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Max pallets"><Input type="number" min="0" value={form.max_pallets} onChange={set('max_pallets')} /></Field>
          <Field label="Max weight (kg)"><Input type="number" min="0" value={form.max_weight_kg} onChange={set('max_weight_kg')} /></Field>
        </div>
        <Field label="What's normally kept here">
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            rows={3} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', resize: 'vertical' }}
            placeholder="Reference label — e.g. 'Bellamie Bottles' — separate from live stock levels" />
        </Field>
        {err && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{err}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
          {bay ? <Btn type="button" variant="danger" onClick={handleDelete} disabled={saving}>Delete</Btn> : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn type="submit" disabled={saving}>{saving ? 'Saving…' : bay ? 'Save' : 'Add Bay'}</Btn>
          </div>
        </div>
      </form>
    </Modal>
  );
}

// ── Bay actions — small chooser shown when a bay is clicked ──────────────
// Clicking a bay on the map used to jump straight into the structural
// "Edit Bay" form. That's the wrong first stop for the far more common
// reason someone clicks a bay: they've got a delivery or a stock take
// correction in hand and want to put stock IN that bay. This chooser
// makes both intents one click away instead of burying "add stock" back
// on the item's own Stock page (see Add Stock modal, below).
function BayActionsModal({ bay, onAddStock, onRemoveStock, onCreatePallet, onFullEdit, onClose }) {
  const contents = bay.contents ?? [];
  const units = bay.storage_units ?? [];
  return (
    <Modal title={bay.code} onClose={onClose} width={380}>
      {(contents.length > 0 || units.length > 0) && (
        <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {contents.map((c, i) => (
            <div key={i} style={{ fontSize: 13, color: 'var(--text)' }}>{c.item_name} <span style={{ color: 'var(--text-muted)' }}>({c.quantity}{c.unit})</span></div>
          ))}
          {units.map((u, i) => (
            <div key={`u${i}`} style={{ fontSize: 13, color: 'var(--text)' }}>{u.unit_ref}{u.item_name ? ` · ${u.item_name}` : ''}</div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Btn onClick={onAddStock}>+ Add Stock to This Bay</Btn>
        {contents.length > 0 && (
          <Btn variant="ghost" onClick={onRemoveStock} style={{ color: '#dc2626' }}>− Remove Stock from This Bay</Btn>
        )}
        <Btn variant="ghost" onClick={onCreatePallet}>📦 Record a Pallet Here</Btn>
        <Btn variant="ghost" onClick={onFullEdit}>⚙ Edit Bay Details</Btn>
      </div>
    </Modal>
  );
}

// ── Remove Stock modal — draw loose (unpalletised) stock off a bay ───────
// Only offered when the bay has contents to draw from (see the "−" button
// above). This is deliberately for stock recorded straight against the
// bay in stock_levels, not stock sat on a pallet — see AddStockModal's
// symmetric "up" counterpart, and CreatePalletModal for turning loose
// stock into a tracked pallet instead of drawing it down.
function RemoveStockModal({ bay, onClose, onSaved }) {
  const contents = bay.contents ?? [];
  const [stockItemId, setStockItemId] = useState(contents[0]?.stock_item_id ?? '');
  const [qty, setQty] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const selected = contents.find(c => c.stock_item_id === stockItemId);

  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      if (!stockItemId) throw new Error('Pick which item to remove');
      if (!qty) throw new Error('Quantity is required');
      if (selected && parseFloat(qty) > selected.quantity) {
        throw new Error(`Only ${selected.quantity} ${selected.unit} of this recorded at ${bay.code}`);
      }

      await apiFetch(`/stock/${stockItemId}/adjust`, {
        method: 'POST',
        body: { quantity: parseFloat(qty), direction: 'down', location_id: bay.id, notes: notes || `Removed from ${bay.code} from the warehouse map` },
      });
      onSaved();
      onClose();
    } catch (e2) { setErr(e2.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal title={`Remove Stock — ${bay.code}`} onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="Item *">
          <Select value={stockItemId} onChange={e => setStockItemId(e.target.value)} required>
            <option value="">— Select an item —</option>
            {contents.map(c => (
              <option key={c.stock_item_id} value={c.stock_item_id}>{c.item_name} ({c.quantity}{c.unit} recorded)</option>
            ))}
          </Select>
        </Field>
        <Field label={`Quantity to remove${selected ? ` (${selected.unit})` : ''} *`}>
          <Input type="number" step="any" min="0" max={selected?.quantity} value={qty} onChange={e => setQty(e.target.value)} required />
        </Field>
        <Field label="Notes"><Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional — e.g. reason" /></Field>

        {err && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{err}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" variant="danger" disabled={saving}>{saving ? 'Removing…' : 'Remove Stock'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Create Pallet modal — record an existing pallet at this bay ──────────
// For stock that's already correctly counted somewhere in Blake and has
// just been palletised — NOT for brand-new stock (use "Add Stock to This
// Bay" for that). This never touches stock_levels: it only creates the
// storage_units row and its storage_unit_contents breakdown, exactly like
// creating a unit from the Storage Units page followed by setting its
// contents — just started from the bay it's actually going into, with a
// label ready to print straight after.
function CreatePalletModal({ bay, onClose, onSaved }) {
  const [itemSearch, setItemSearch] = useState('');
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [unitType, setUnitType] = useState('pallet');
  const [qty, setQty] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [created, setCreated] = useState(null); // { id, unit_ref } once saved

  // Where this pallet's stock is actually coming from — e.g. the
  // Unallocated pool a stock take gain landed in, or another bay that's
  // being re-palletised. Fetched fresh whenever the selected item changes,
  // from the same stock_summary the Stock page uses, so it's always the
  // real current distribution rather than something guessed client-side.
  const [sources, setSources] = useState([]); // [{location_id, location_code, quantity_on_hand}]
  const [sourceLocationId, setSourceLocationId] = useState('');

  useEffect(() => {
    if (!itemSearch.trim()) { setItems([]); return; }
    const t = setTimeout(async () => {
      try {
        const { stock_items } = await apiFetch(`/stock?search=${encodeURIComponent(itemSearch)}`);
        setItems(stock_items || []);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [itemSearch]);

  useEffect(() => {
    setSources([]); setSourceLocationId('');
    if (!selectedItem) return;
    (async () => {
      try {
        const data = await apiFetch(`/stock/${selectedItem.id}`);
        const locs = (data.stock_summary?.locations || []).filter(l => parseFloat(l.quantity_on_hand) > 0);
        setSources(locs);
        if (locs.length > 0) setSourceLocationId(locs[0].location_id);
      } catch { /* ignore */ }
    })();
  }, [selectedItem]);

  const selectedSource = sources.find(s => s.location_id === sourceLocationId);

  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      if (!selectedItem) throw new Error('Pick the stock item this pallet holds');
      if (!qty) throw new Error('Quantity is required');
      if (sources.length > 0) {
        if (!sourceLocationId) throw new Error('Pick where this stock is coming from');
        if (selectedSource && parseFloat(qty) > parseFloat(selectedSource.quantity_on_hand)) {
          throw new Error(`Only ${selectedSource.quantity_on_hand} recorded at ${selectedSource.location_code}`);
        }
      }

      const { storage_unit } = await apiFetch('/storage-units', {
        method: 'POST',
        body: {
          unit_type: unitType,
          location_id: bay.id,
          stock_item_id: selectedItem.id,
          quantity: parseFloat(qty),
          // Moves the quantity out of wherever it's currently recorded and
          // onto this bay — a real stock_levels transfer with no W1 impact,
          // not just a label. Omitted (no known location for the item at
          // all yet) falls back to the old label-only behaviour.
          source_location_id: sourceLocationId || undefined,
        },
      });
      // storage_units.quantity is just the quick-reference summary the map
      // reads — storage_unit_contents is what everything else (stock take
      // pallet reconciliation, contents editing, manifests) actually uses,
      // so both need writing.
      // With a source, creating the unit already moved the stock onto it.
      // With none (Blake has none of it anywhere yet), it's only recorded on
      // the pallet — Storage Units flags it until the stock is booked in.
      if (!sourceLocationId) {
        await apiFetch(`/storage-units/${storage_unit.id}/contents`, {
          method: 'POST',
          body: { contents: [{ stock_item_id: selectedItem.id, quantity: parseFloat(qty) }] },
        });
      }

      setCreated(storage_unit);
      onSaved();
    } catch (e2) { setErr(e2.message); }
    finally { setSaving(false); }
  }

  async function printLabel() {
    try {
      const token = localStorage.getItem('pld_token');
      const res = await fetch(`/api/storage-units/${created.id}/label.pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to load pallet label PDF');
      const blob = await res.blob();
      const file = new File([blob], `${created.unit_ref}-label.pdf`, { type: 'application/pdf' });
      window.open(URL.createObjectURL(file), '_blank');
    } catch (e2) { setErr(e2.message); }
  }

  if (created) {
    return (
      <Modal title="Pallet recorded" onClose={onClose} width={420}>
        <p style={{ fontSize: 14, color: 'var(--text)', margin: '0 0 16px' }}>
          <strong>{created.unit_ref}</strong> is now recorded at {bay.code}.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn variant="ghost" onClick={onClose}>Done</Btn>
          <Btn variant="ghost" onClick={printLabel}>Preview label</Btn>
          <PrintButton docType="pallet_label" path={`/storage-units/${created.id}/label.pdf`} title={`Pallet label ${created.unit_ref}`} label="Print label" size="md" />
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={`Record a Pallet — ${bay.code}`} onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="Pallet type">
          <Select value={unitType} onChange={e => setUnitType(e.target.value)}>
            {['pallet', 'ibc', 'cask', 'case'].map(t => <option key={t} value={t}>{t}</option>)}
          </Select>
        </Field>
        <Field label="Stock item *">
          <Input placeholder="Search existing items…" value={itemSearch} onChange={e => { setItemSearch(e.target.value); setSelectedItem(null); }} />
          {items.length > 0 && !selectedItem && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginTop: 4, maxHeight: 160, overflowY: 'auto' }}>
              {items.map(i => (
                <div key={i.id} onClick={() => { setSelectedItem(i); setItemSearch(i.name); setItems([]); }}
                  style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
                  {i.name} <span style={{ color: 'var(--text-muted)' }}>({i.sku})</span>
                </div>
              ))}
            </div>
          )}
          {selectedItem && <p style={{ fontSize: 12, color: 'var(--pld-green)', margin: '4px 0 0' }}>✓ {selectedItem.name} selected</p>}
        </Field>
        {sources.length > 0 && (
          <Field label="Draw from *">
            <Select value={sourceLocationId} onChange={e => setSourceLocationId(e.target.value)}>
              {sources.map(s => (
                <option key={s.location_id} value={s.location_id}>{s.location_code} ({s.quantity_on_hand} recorded)</option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Quantity on this pallet *"><Input type="number" step="any" min="0" max={selectedSource?.quantity_on_hand} value={qty} onChange={e => setQty(e.target.value)} required /></Field>
        {sources.length > 0 ? (
          <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--text-muted)' }}>
            Moves this quantity out of {selectedSource?.location_code || 'the source'} and onto this pallet at {bay.code}. Total stock on hand doesn't change, only which bay it's recorded in — no W1 or duty impact.
          </p>
        ) : (
          <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--text-muted)' }}>
            No recorded location has stock of this item yet, so this will just label {bay.code} without moving anything — add stock somewhere first if this quantity isn't accounted for yet.
          </p>
        )}
        {err && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{err}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Recording…' : 'Record Pallet'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Add Stock modal — put stock into a specific bay from the map ─────────
// A thin wrapper over POST /stock/:id/adjust (direction "up"), the same
// endpoint the Stock page's own "Adjust Stock" action uses — this just
// starts from the bay instead of the item, and the bay is already fixed
// so there's one less thing to get wrong. Existing item vs. new item
// mirrors StockTake.jsx's AddCountModal, since it's the same "search or
// create" shape staff already know from counting stock.
function AddStockModal({ bay, onClose, onSaved }) {
  const [itemSearch, setItemSearch] = useState('');
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newItem, setNewItem] = useState({ name: '', category: 'packaging', unit: 'unit' });
  const [qty, setQty] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [justAdded, setJustAdded] = useState(null);

  useEffect(() => {
    if (!itemSearch.trim()) { setItems([]); return; }
    const t = setTimeout(async () => {
      try {
        const { stock_items } = await apiFetch(`/stock?search=${encodeURIComponent(itemSearch)}`);
        setItems(stock_items || []);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [itemSearch]);

  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr(''); setJustAdded(null);
    try {
      let stockItemId = selectedItem?.id;
      if (creatingNew) {
        if (!newItem.name.trim()) throw new Error('Item name is required');
        const { stock_item } = await apiFetch('/stock', { method: 'POST', body: newItem });
        stockItemId = stock_item.id;
      }
      if (!stockItemId) throw new Error('Pick an existing item or add a new one');
      if (!qty) throw new Error('Quantity is required');

      await apiFetch(`/stock/${stockItemId}/adjust`, {
        method: 'POST',
        body: { quantity: parseFloat(qty), direction: 'up', location_id: bay.id, notes: notes || `Added to ${bay.code} from the warehouse map` },
      });
      onSaved();

      // Reset for the next item — you're almost always adding several
      // things to the same bay in one delivery, same reasoning as the
      // stock take Add Count modal.
      setJustAdded(selectedItem?.name || newItem.name);
      setItemSearch('');
      setSelectedItem(null);
      setCreatingNew(false);
      setNewItem({ name: '', category: 'packaging', unit: 'unit' });
      setQty('');
      setNotes('');
    } catch (e2) { setErr(e2.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal title={`Add Stock — ${bay.code}`} onClose={onClose}>
      <form onSubmit={submit}>
        {justAdded && (
          <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--pld-green)', fontWeight: 600 }}>✓ Added {justAdded}. Add another, or close when done.</p>
        )}

        {!creatingNew ? (
          <Field label="Item">
            <Input placeholder="Search existing items…" value={itemSearch} onChange={e => { setItemSearch(e.target.value); setSelectedItem(null); }} />
            {items.length > 0 && !selectedItem && (
              <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginTop: 4, maxHeight: 160, overflowY: 'auto' }}>
                {items.map(i => (
                  <div key={i.id} onClick={() => { setSelectedItem(i); setItemSearch(i.name); setItems([]); }}
                    style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
                    {i.name} <span style={{ color: 'var(--text-muted)' }}>({i.sku})</span>
                  </div>
                ))}
              </div>
            )}
            {selectedItem && <p style={{ fontSize: 12, color: 'var(--pld-green)', margin: '4px 0 0' }}>✓ {selectedItem.name} selected</p>}
            <button type="button" onClick={() => { setCreatingNew(true); setSelectedItem(null); }}
              style={{ background: 'none', border: 'none', color: 'var(--pld-green)', fontSize: 12, cursor: 'pointer', padding: '6px 0 0', textDecoration: 'underline' }}>
              Can't find it — add a new item
            </button>
          </Field>
        ) : (
          <>
            <Field label="New item name *"><Input value={newItem.name} onChange={e => setNewItem(f => ({ ...f, name: e.target.value }))} required /></Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="Category">
                <Select value={newItem.category} onChange={e => setNewItem(f => ({ ...f, category: e.target.value }))}>
                  {['raw_material', 'semi_product', 'bulk_finished', 'bottle_finished', 'packaging', 'consumable', 'by_product', 'client_stock'].map(c => (
                    <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Unit"><Input value={newItem.unit} onChange={e => setNewItem(f => ({ ...f, unit: e.target.value }))} placeholder="e.g. unit, case, kg" /></Field>
            </div>
            <button type="button" onClick={() => setCreatingNew(false)}
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer', padding: '0 0 10px', textDecoration: 'underline' }}>
              ← search existing items instead
            </button>
          </>
        )}

        <Field label="Quantity *"><Input type="number" step="any" min="0" value={qty} onChange={e => setQty(e.target.value)} required /></Field>
        <Field label="Notes"><Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional — e.g. delivery ref" /></Field>

        {err && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{err}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Done</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Adding…' : 'Add Stock'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Add Facility modal (first-run bootstrap) ──────────────────────────────
function FacilityModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', address: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      await apiFetch('/warehouse/facilities', { method: 'POST', body: form });
      onSaved();
    } catch (e2) { setErr(e2.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal title="Add Facility" onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="Name *"><Input value={form.name} onChange={set('name')} placeholder="e.g. Pleasant Land Distillery" required /></Field>
        <Field label="Address"><Input value={form.address} onChange={set('address')} placeholder="Optional" /></Field>
        {err && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{err}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving…' : 'Add Facility'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function bayMatchesSearch(bay, search) {
  if (!search.trim()) return true;
  const s = search.toLowerCase();
  return (
    bay.code?.toLowerCase().includes(s) ||
    bay.notes?.toLowerCase().includes(s) ||
    (bay.contents ?? []).some(c => c.item_name?.toLowerCase().includes(s)) ||
    (bay.storage_units ?? []).some(u => u.item_name?.toLowerCase().includes(s) || u.unit_ref?.toLowerCase().includes(s))
  );
}

// ── Grid cell — a single bay, inline-editable ─────────────────────────────
function GridCell({ bay, search, onFullEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(bay.notes ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!editing) setValue(bay.notes ?? ''); }, [bay.notes, editing]);

  const contents = bay.contents ?? [];
  const units = bay.storage_units ?? [];
  const occupied = contents.length > 0 || units.length > 0;
  const dimmed = search.trim() && !bayMatchesSearch(bay, search);

  async function save() {
    if (value === (bay.notes ?? '')) { setEditing(false); return; }
    setSaving(true);
    try {
      await apiFetch(`/warehouse/locations/${bay.id}`, { method: 'PUT', body: { notes: value } });
      onSaved();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); setEditing(false); }
  }

  return (
    <div
      style={{
        background: occupied ? '#f0fdf4' : 'var(--surface)',
        border: '1px solid ' + (dimmed ? 'var(--border)' : occupied ? '#bbf7d0' : 'var(--border)'),
        borderRadius: 'var(--radius)', padding: '8px 10px', minHeight: 76,
        opacity: dimmed ? 0.3 : 1, display: 'flex', flexDirection: 'column', gap: 4,
        transition: 'opacity .15s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 4 }}>
        <span style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bay.code}</span>
        <button onClick={() => onFullEdit(bay)} title="Full edit" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12, padding: 0, flexShrink: 0, lineHeight: 1 }}>⚙</button>
      </div>

      {occupied && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {contents.slice(0, 2).map((c, i) => (
            <div key={i} style={{ fontSize: 11, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {c.item_name} <span style={{ color: 'var(--text-muted)' }}>({c.quantity}{c.unit})</span>
            </div>
          ))}
          {units.slice(0, 2).map((u, i) => (
            <div key={`u${i}`} style={{ fontSize: 11, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {u.unit_ref}{u.item_name ? ` · ${u.item_name}` : ''}
            </div>
          ))}
          {(contents.length + units.length) > 2 && (
            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>+{contents.length + units.length - 2} more</div>
          )}
        </div>
      )}

      {editing ? (
        <textarea
          autoFocus
          value={value}
          onChange={e => setValue(e.target.value)}
          onBlur={save}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); }
            if (e.key === 'Escape') { setValue(bay.notes ?? ''); setEditing(false); }
          }}
          rows={2}
          style={{ fontSize: 11, padding: 4, border: '1px solid var(--pld-green)', borderRadius: 4, resize: 'vertical', width: '100%', boxSizing: 'border-box', background: 'var(--surface-2)', color: 'var(--text)' }}
        />
      ) : (
        <div
          onClick={() => setEditing(true)}
          style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: bay.notes ? 'normal' : 'italic', cursor: 'text', minHeight: 14 }}
        >
          {saving ? 'Saving…' : (bay.notes || (occupied ? '+ note' : 'Click to add note…'))}
        </div>
      )}
    </div>
  );
}

// ── Position column — one bay position within a single rack's elevation ───
// A position (row_ref) can have several bays stacked on it, one per level —
// Bottom/Middle/Top — or per side for the shelf. This renders that stack as
// a small vertical sub-grid within the position's column, per Joe's sketch:
// one zone at a time, positions running left-to-right, each position's
// levels stacked Top (top of column) down to Bottom.
const LEVEL_ORDER = ['Top', 'Middle', 'Bottom', 'In Front'];
function sortByLevel(bays) {
  return [...bays].sort((a, b) => {
    const ka = a.level_ref || a.bay_ref || '', kb = b.level_ref || b.bay_ref || '';
    const ia = LEVEL_ORDER.indexOf(ka), ib = LEVEL_ORDER.indexOf(kb);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return ka.localeCompare(kb);
  });
}

// A bay's max_pallets says how many pallets physically fit on that shelf —
// e.g. a bottom bay holds 2, stacked one above the other per Joe's
// reference sheet. Render one white square per pallet slot rather than a
// single merged cell, so the map actually looks like the rack (each box =
// a pallet spot), even though they're all still part of the same
// underlying bay record. Every box opens the same bay for editing.
function LevelCell({ bay, search, onOpen }) {
  const dimmed = search.trim() && !bayMatchesSearch(bay, search);
  const slots = Math.max(1, bay.max_pallets ?? 1);
  const contents = bay.contents ?? [];
  const units = bay.storage_units ?? [];
  const occupants = [...contents.map(c => c.item_name), ...units.map(u => u.unit_ref)];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, opacity: dimmed ? 0.25 : 1, transition: 'opacity .15s' }}>
      {Array.from({ length: slots }).map((_, i) => {
        const label = occupants[i];
        const occupied = !!label;
        return (
          <button
            key={i}
            onClick={() => onOpen(bay)}
            title={bay.code}
            style={{
              // width:100% + aspect-ratio (no flex-grow) — a flex item with
              // flex:1 inside a column container gets its height from
              // flex-basis/flex-grow, which overrides aspect-ratio and was
              // squashing every cell into a short rectangle instead of a
              // true pallet-sized square. Sizing off width keeps cells
              // square and to scale with however wide the rack is drawn.
              width: '100%', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid ' + (occupied ? '#bbf7d0' : '#c9b199'),
              background: occupied ? '#f0fdf4' : 'var(--surface)',
              borderRadius: 3, cursor: 'pointer', padding: '2px 3px', overflow: 'hidden',
            }}
          >
            <span style={{ fontSize: 9, lineHeight: '11px', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>
              {label || <span style={{ color: 'var(--text-muted)' }}>—</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ── Zone rack view — one zone at a time, "stood in front of it" ──────────
// Landscape strip. Positions (row_ref) run left to right in walking order,
// numbered along the bottom. Each position is a column showing its levels
// stacked Top-to-Bottom — matching Joe's own sketch of Rack A.
function sortPositions(positions) {
  return [...positions].sort((a, b) => {
    const na = parseFloat(a), nb = parseFloat(b);
    if (!isNaN(na) && !isNaN(nb)) return na - nb;
    return String(a).localeCompare(String(b));
  });
}

function ZoneRackView({ bays, search, onOpenBay }) {
  const positions = useMemo(() => sortPositions([...new Set(bays.map(b => b.row_ref ?? '—'))]), [bays]);
  const byPosition = useMemo(() => {
    const m = {};
    bays.forEach(b => { const k = b.row_ref ?? '—'; (m[k] = m[k] || []).push(b); });
    Object.keys(m).forEach(k => { m[k] = sortByLevel(m[k]); });
    return m;
  }, [bays]);

  if (positions.length === 0) {
    return <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No bays in this zone yet.</p>;
  }

  // Fixed-width columns forced horizontal scrolling once a rack had more
  // than a handful of positions. Every column now flexes to share the
  // available width instead, so the whole rack always fits on screen. Each
  // bay cell is square (aspect-ratio 1:1) rather than a fixed pixel height,
  // per Joe's reference sheet.
  // Every position column stacks its own levels bottom-aligned (so a
  // 2-level position and a 4-level position both sit on the same "floor"
  // rail rather than floating at different heights), and gets a visible
  // upright divider on its right edge — like the physical bay dividers on
  // the rack — so neighbouring bays read as separate slots rather than one
  // continuous strip.
  // Columns flex to fill the rack's width, but a zone with only a couple
  // of positions (e.g. Back Wall L) would otherwise stretch each one to
  // fill the whole row — capping the column width keeps pallet cells a
  // consistent, believable size regardless of how few positions a zone
  // has, and centres the group instead of blowing it up.
  return (
    <div style={{ display: 'flex', gap: 0, justifyContent: 'center', border: '3px solid #8b5a2b', borderRadius: 8, padding: '14px 10px 6px', background: '#f5ebe0' }}>
      {positions.map((pos, i) => (
        <div
          key={pos}
          style={{
            flex: '1 1 0', minWidth: 0, maxWidth: 150, display: 'flex', flexDirection: 'column', gap: 3,
            padding: '0 6px',
            borderRight: i < positions.length - 1 ? '2px solid #c8a578' : 'none',
          }}
        >
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 3 }}>
            {byPosition[pos].map(bay => (
              <LevelCell key={bay.id} bay={bay} search={search} onOpen={onOpenBay} />
            ))}
          </div>
          <div style={{ height: 6, background: '#8b5a2b', borderRadius: 2 }} />
          <div style={{ textAlign: 'center', fontSize: 11, color: '#8b5a2b', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pos}</div>
        </div>
      ))}
    </div>
  );
}

// ── Zone tabs — pick which zone's rack to look at ─────────────────────────
const BARN_ZONE_ORDER = ['SHELF', 'RACKA', 'WALLL', 'RACKB', 'RACKC', 'WALLR', 'RACKD'];

function ZoneTabs({ zones, activeZoneId, onSelect }) {
  const sorted = useMemo(() => {
    return [...zones].sort((a, b) => {
      const ia = BARN_ZONE_ORDER.indexOf(a.code), ib = BARN_ZONE_ORDER.indexOf(b.code);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [zones]);

  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
      {sorted.map(z => (
        <button
          key={z.id}
          onClick={() => onSelect(z.id)}
          style={{
            padding: '6px 14px', borderRadius: 99, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            border: '1px solid ' + (z.id === activeZoneId ? 'var(--pld-green)' : 'var(--border)'),
            background: z.id === activeZoneId ? 'var(--pld-green)' : 'var(--surface-2)',
            color: z.id === activeZoneId ? '#fff' : 'var(--text)',
          }}
        >
          {z.name}
        </button>
      ))}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function Warehouse() {
  const { data: facData, refetch: refetchFac } = useApi('/warehouse/facilities');
  // Cow Shed is the one facility with a real map — put it first regardless
  // of alphabetical order, so it's the default view rather than whichever
  // simple location happens to sort first.
  const facilitiesRaw = facData?.facilities ?? facData ?? [];
  const facilities = useMemo(() => {
    return [...facilitiesRaw].sort((a, b) => {
      if (a.name === 'Cow Shed') return -1;
      if (b.name === 'Cow Shed') return 1;
      return a.name.localeCompare(b.name);
    });
  }, [facilitiesRaw]);

  const [facilityId, setFacilityId] = useState(null);
  const [zoneId,     setZoneId]     = useState(null);
  const [search,     setSearch]     = useState('');

  const activeFacilityId = facilityId ?? facilities[0]?.id ?? null;

  const { data: zoneData, refetch: refetchZones } = useApi(
    activeFacilityId ? `/warehouse/zones?facility_id=${activeFacilityId}` : null,
    [activeFacilityId]
  );
  const zones = zoneData?.zones ?? zoneData ?? [];
  const activeZoneId = zoneId ?? zones[0]?.id ?? null;

  // Fetch every bay in the facility once — used both to build the floor
  // plan's per-zone occupancy counts and (sliced client-side) as the active
  // zone's bay grid. Avoids a separate round-trip per zone.
  const { data: locData, refetch: refetchLocs } = useApi(
    activeFacilityId ? `/warehouse/locations?facility_id=${activeFacilityId}` : null,
    [activeFacilityId]
  );
  const allBays = locData?.locations ?? locData ?? [];
  const bays = useMemo(() => allBays.filter(b => b.zone_id === activeZoneId), [allBays, activeZoneId]);

  const [showFacilityModal, setShowFacilityModal] = useState(false);
  const [showZoneModal, setShowZoneModal] = useState(false);
  const [bayModal, setBayModal] = useState(null); // null | { zoneId, bay } | { zoneId, bay: null }
  const [bayActionsBay, setBayActionsBay] = useState(null); // bay | null — chooser shown on click
  const [addStockBay, setAddStockBay] = useState(null); // bay | null
  const [removeStockBay, setRemoveStockBay] = useState(null); // bay | null
  const [createPalletBay, setCreatePalletBay] = useState(null); // bay | null

  const isOccupied = b => (b.contents?.length ?? 0) > 0 || (b.storage_units?.length ?? 0) > 0;
  const occupiedCount = allBays.filter(isOccupied).length;

  async function handleDeleteFacility() {
    const facility = facilities.find(f => f.id === activeFacilityId);
    if (!facility) return;
    if (!(await uiConfirm(`Delete facility "${facility.name}" and everything in it (all its zones and bays)? This won't remove any stock history.`))) return;
    try {
      await apiFetch(`/warehouse/facilities/${facility.id}`, { method: 'DELETE' });
      setFacilityId(null);
      setZoneId(null);
      refetchFac();
    } catch (e) { alert(e.message); }
  }

  async function handleDeleteZone(zone) {
    const zoneBayCount = allBays.filter(b => b.zone_id === zone.id).length;
    if (!(await uiConfirm(`Delete zone "${zone.name}" and all ${zoneBayCount} bay(s) in it? This won't remove any stock history.`))) return;
    try {
      await apiFetch(`/warehouse/zones/${zone.id}`, { method: 'DELETE' });
      refetchZones();
      refetchLocs();
    } catch (e) { alert(e.message); }
  }

  if (facData && facilities.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <h2 style={{ margin: '0 0 8px', color: 'var(--text)' }}>No facility set up yet</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>Add your site to start mapping storage zones and bays.</p>
        <Btn onClick={() => setShowFacilityModal(true)}>+ Add Facility</Btn>
        {showFacilityModal && (
          <FacilityModal onClose={() => setShowFacilityModal(false)} onSaved={() => { setShowFacilityModal(false); refetchFac(); }} />
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 28px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>Warehouse Map</h2>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {facilities.length > 1 && (
              <>
                <Select value={activeFacilityId ?? ''} onChange={e => { setFacilityId(e.target.value); setZoneId(null); }} style={{ display: 'inline-block', width: 'auto', marginRight: 6 }}>
                  {facilities.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </Select>
                <button onClick={handleDeleteFacility} title="Delete this facility" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 12, marginRight: 8, textDecoration: 'underline', padding: 0 }}>
                  delete
                </button>
              </>
            )}
            {zones.length} zone{zones.length === 1 ? '' : 's'} · {allBays.length} bay{allBays.length === 1 ? '' : 's'} · {occupiedCount} occupied
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Input placeholder="Search bays or contents…" value={search} onChange={e => setSearch(e.target.value)} style={{ width: 220 }} />
          <Btn variant="ghost" size="sm" onClick={() => setShowZoneModal(true)}>+ Add Zone</Btn>
        </div>
      </div>

      {zones.length === 0 && (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
          No zones yet in this facility. Add one to start laying out bays (e.g. "Left Racking", "Centre Racking", "IBC Stacks").
        </div>
      )}

      {zones.length > 0 && (
        <>
          <ZoneTabs zones={zones} activeZoneId={activeZoneId} onSelect={setZoneId} />

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
                {zones.find(z => z.id === activeZoneId)?.name}
              </h3>
              {(() => {
                // Total pallet capacity across every level — e.g. Rack A's
                // 13 bottom bays x2 + 13 middle x1 + 13 top x1 = 52.
                const capacity = bays.reduce((s, b) => s + Math.max(1, b.max_pallets ?? 1), 0);
                return capacity > 0 ? <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{capacity} pallet{capacity === 1 ? '' : 's'} capacity</span> : null;
              })()}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn size="sm" variant="ghost" onClick={() => handleDeleteZone(zones.find(z => z.id === activeZoneId))} style={{ color: '#dc2626' }}>Delete Zone</Btn>
              <Btn size="sm" onClick={() => setBayModal({ zoneId: activeZoneId, bay: null })}>+ Add Bay</Btn>
            </div>
          </div>

          <ZoneRackView bays={bays} search={search} onOpenBay={bay => setBayActionsBay(bay)} />
        </>
      )}

      {/* Modals */}
      {showZoneModal && (
        <ZoneModal facilityId={activeFacilityId} onClose={() => setShowZoneModal(false)} onSaved={() => { setShowZoneModal(false); refetchZones(); }} />
      )}
      {bayModal && (
        <BayModal
          zoneId={bayModal.zoneId}
          bay={bayModal.bay}
          onClose={() => setBayModal(null)}
          onSaved={() => { setBayModal(null); refetchLocs(); }}
        />
      )}
      {bayActionsBay && (
        <BayActionsModal
          bay={bayActionsBay}
          onClose={() => setBayActionsBay(null)}
          onAddStock={() => { setAddStockBay(bayActionsBay); setBayActionsBay(null); }}
          onRemoveStock={() => { setRemoveStockBay(bayActionsBay); setBayActionsBay(null); }}
          onCreatePallet={() => { setCreatePalletBay(bayActionsBay); setBayActionsBay(null); }}
          onFullEdit={() => { setBayModal({ zoneId: bayActionsBay.zone_id, bay: bayActionsBay }); setBayActionsBay(null); }}
        />
      )}
      {addStockBay && (
        <AddStockModal
          bay={addStockBay}
          onClose={() => { setAddStockBay(null); refetchLocs(); }}
          onSaved={refetchLocs}
        />
      )}
      {removeStockBay && (
        <RemoveStockModal
          bay={removeStockBay}
          onClose={() => setRemoveStockBay(null)}
          onSaved={refetchLocs}
        />
      )}
      {createPalletBay && (
        <CreatePalletModal
          bay={createPalletBay}
          onClose={() => { setCreatePalletBay(null); refetchLocs(); }}
          onSaved={refetchLocs}
        />
      )}
    </div>
  );
}
